import React, { useEffect, useMemo, useState } from 'react';
import { eventsService } from '../services';
import './EventsCalendarTab.css';

const EVENT_TYPE_OPTIONS = [
  { value: 'PROVA', label: 'Prova', icon: '📘' },
  { value: 'TRABALHO', label: 'Trabalho', icon: '📝' },
  { value: 'APRESENTACAO', label: 'Apresentação', icon: '🎤' },
  { value: 'EXTRACURRICULAR', label: 'Extracurricular', icon: '⚽' },
  { value: 'OUTRO', label: 'Outro', icon: '📅' }
];

const ICON_BY_TYPE = EVENT_TYPE_OPTIONS.reduce((acc, item) => {
  acc[item.value] = item.icon;
  return acc;
}, {});

const pad = (n) => String(n).padStart(2, '0');

const toDateKey = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const toDateInput = (date) => toDateKey(date);

const monthLabel = (date) =>
  date.toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric'
  });

const defaultForm = {
  title: '',
  event_type: 'PROVA',
  date: '',
  time: '',
  all_day: true,
  subject: '',
  description: '',
  create_task: false,
  create_study_mission: false,
  child_visible: true
};

export default function EventsCalendarTab({ mode, childId, onViewTask, onViewStudyMission }) {
  const isParentMode = mode === 'parent';
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [events, setEvents] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isStudyType = form.event_type === 'PROVA' || form.event_type === 'TRABALHO';
  const studyMissionAllowed = isStudyType && form.subject.trim().length > 0;

  useEffect(() => {
    if (!studyMissionAllowed && form.create_study_mission) {
      setForm((prev) => ({ ...prev, create_study_mission: false }));
    }
  }, [studyMissionAllowed, form.create_study_mission]);

  const loadEvents = async () => {
    if (isParentMode && !childId) {
      return;
    }

    setLoading(true);
    setError('');

    const from = toDateInput(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1));
    const to = toDateInput(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0));

    try {
      const [rangeResp, upcomingResp] = await Promise.all([
        isParentMode
          ? eventsService.getChildEvents(childId, { from, to })
          : eventsService.getMyEvents({ from, to }),
        isParentMode
          ? eventsService.getChildUpcomingEvents(childId, 14)
          : eventsService.getMyUpcomingEvents(14)
      ]);

      const monthEvents = rangeResp.data?.events || [];
      const nextEvents = upcomingResp.data?.events || [];
      setEvents(monthEvents);
      setUpcoming(nextEvents);

      if (selectedEvent) {
        const stillExists = [...monthEvents, ...nextEvents].find((evt) => evt.id === selectedEvent.id);
        setSelectedEvent(stillExists || null);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar compromissos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId, currentMonth, mode]);

  const eventsByDate = useMemo(() => {
    const grouped = {};
    events.forEach((evt) => {
      const key = toDateKey(new Date(evt.start_at));
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(evt);
    });
    return grouped;
  }, [events]);

  const monthGrid = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const totalDays = new Date(year, month + 1, 0).getDate();

    const cells = [];
    for (let i = 0; i < firstDay; i += 1) cells.push(null);
    for (let day = 1; day <= totalDays; day += 1) cells.push(new Date(year, month, day));
    return cells;
  }, [currentMonth]);

  const resetForm = () => {
    setForm(defaultForm);
    setEditingId(null);
  };

  const openCreateForm = () => {
    resetForm();
    setShowForm(true);
  };

  const openEditForm = (evt) => {
    const eventStart = new Date(evt.start_at);
    setForm({
      title: evt.title || '',
      event_type: evt.event_type || 'OUTRO',
      date: toDateInput(eventStart),
      time: evt.all_day ? '' : `${pad(eventStart.getHours())}:${pad(eventStart.getMinutes())}`,
      all_day: evt.all_day,
      subject: evt.subject || '',
      description: evt.description || '',
      create_task: !!evt.create_task,
      create_study_mission: !!evt.create_study_mission,
      child_visible: evt.visibility !== 'PARENT_ONLY'
    });
    setEditingId(evt.id);
    setShowForm(true);
  };

  const buildPayload = () => ({
    title: form.title,
    event_type: form.event_type,
    start_at: form.all_day || !form.time ? form.date : `${form.date}T${form.time}:00`,
    all_day: form.all_day,
    subject: isStudyType ? form.subject : null,
    description: form.description || null,
    create_task: form.create_task,
    create_study_mission: studyMissionAllowed && form.create_study_mission,
    visibility: form.child_visible ? 'CHILD_VISIBLE' : 'PARENT_ONLY'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.title.trim() || !form.date) {
      setError('Título e data são obrigatórios');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = buildPayload();

      if (editingId) {
        await eventsService.updateChildEvent(childId, editingId, payload);
      } else {
        await eventsService.createChildEvent(childId, payload);
      }

      setShowForm(false);
      resetForm();
      await loadEvents();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao salvar compromisso');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Deseja cancelar este compromisso?')) return;

    setLoading(true);
    setError('');

    try {
      await eventsService.deleteChildEvent(childId, eventId);
      setSelectedEvent(null);
      await loadEvents();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao cancelar compromisso');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="events-tab">
      <div className="events-topbar">
        <div className="events-month-nav">
          <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}>◀</button>
          <strong>{monthLabel(currentMonth)}</strong>
          <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}>▶</button>
        </div>
        {isParentMode && (
          <button className="add-task-btn" onClick={openCreateForm}>
            + Novo compromisso
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && isParentMode && (
        <form className="event-form" onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="Título"
            value={form.title}
            onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
            required
          />

          <select
            value={form.event_type}
            onChange={(e) => setForm((prev) => ({ ...prev, event_type: e.target.value }))}
          >
            {EVENT_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.icon} {opt.label}</option>
            ))}
          </select>

          <div className="event-form-row">
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))}
              required
            />
            {!form.all_day && (
              <input
                type="time"
                value={form.time}
                onChange={(e) => setForm((prev) => ({ ...prev, time: e.target.value }))}
              />
            )}
          </div>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={form.all_day}
              onChange={(e) => setForm((prev) => ({ ...prev, all_day: e.target.checked }))}
            />
            <span>Dia inteiro</span>
          </label>

          {isStudyType && (
            <input
              type="text"
              placeholder="Matéria"
              value={form.subject}
              onChange={(e) => setForm((prev) => ({ ...prev, subject: e.target.value }))}
              required={form.create_study_mission}
            />
          )}

          <textarea
            placeholder="Descrição (opcional)"
            value={form.description}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
          />

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={form.create_task}
              onChange={(e) => setForm((prev) => ({ ...prev, create_task: e.target.checked }))}
            />
            <span>Criar tarefa</span>
          </label>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={form.create_study_mission}
              disabled={!studyMissionAllowed}
              onChange={(e) => setForm((prev) => ({ ...prev, create_study_mission: e.target.checked }))}
            />
            <span>Criar missão de estudo</span>
          </label>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={form.child_visible}
              onChange={(e) => setForm((prev) => ({ ...prev, child_visible: e.target.checked }))}
            />
            <span>A criança vê este compromisso</span>
          </label>

          <div className="event-form-actions">
            <button type="submit" disabled={loading}>{editingId ? 'Salvar' : 'Criar'}</button>
            <button type="button" onClick={() => { setShowForm(false); resetForm(); }}>Cancelar</button>
          </div>
        </form>
      )}

      <div className="events-grid-calendar">
        <div className="events-weekdays">
          <span>Dom</span><span>Seg</span><span>Ter</span><span>Qua</span><span>Qui</span><span>Sex</span><span>Sáb</span>
        </div>
        <div className="events-days-grid">
          {monthGrid.map((day, idx) => {
            if (!day) return <div key={`blank-${idx}`} className="events-day empty" />;

            const key = toDateKey(day);
            const dayEvents = eventsByDate[key] || [];

            return (
              <button
                type="button"
                key={key}
                className="events-day"
                onClick={() => {
                  if (dayEvents.length > 0) {
                    setSelectedEvent(dayEvents[0]);
                  }
                }}
              >
                <span className="day-number">{day.getDate()}</span>
                <div className="day-icons">
                  {dayEvents.slice(0, 3).map((evt) => (
                    <span key={evt.id}>{ICON_BY_TYPE[evt.event_type] || '📅'}</span>
                  ))}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="events-sections">
        <div className="events-upcoming">
          <h4>Próximos 14 dias</h4>
          {upcoming.length === 0 && <p className="empty">Sem compromissos próximos</p>}
          {upcoming.map((evt) => (
            <button
              type="button"
              key={evt.id}
              className="upcoming-item"
              onClick={() => setSelectedEvent(evt)}
            >
              <span>{ICON_BY_TYPE[evt.event_type] || '📅'} {evt.title}</span>
              <small>{new Date(evt.start_at).toLocaleDateString('pt-BR')}</small>
            </button>
          ))}
        </div>

        {selectedEvent && (
          <div className="event-detail">
            <h4>{ICON_BY_TYPE[selectedEvent.event_type] || '📅'} {selectedEvent.title}</h4>
            <p><strong>Tipo:</strong> {selectedEvent.event_type}</p>
            {selectedEvent.subject && <p><strong>Matéria:</strong> {selectedEvent.subject}</p>}
            <p>
              <strong>Data:</strong> {new Date(selectedEvent.start_at).toLocaleString('pt-BR')}
            </p>
            {selectedEvent.description && <p>{selectedEvent.description}</p>}

            <div className="event-detail-actions">
              {selectedEvent.linked_task_id && (
                <button type="button" onClick={() => onViewTask && onViewTask(selectedEvent.linked_task_id)}>
                  Ver tarefa
                </button>
              )}
              {selectedEvent.linked_study_mission_id && (
                <button type="button" onClick={() => onViewStudyMission && onViewStudyMission(selectedEvent.linked_study_mission_id)}>
                  Ver missão de estudo
                </button>
              )}
              {isParentMode && (
                <>
                  <button type="button" onClick={() => openEditForm(selectedEvent)}>Editar</button>
                  <button type="button" className="danger" onClick={() => handleDelete(selectedEvent.id)}>Cancelar</button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
